import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { supabase } from './supabase';
import { Session, User } from '@supabase/supabase-js';

export interface AuthProfile {
    role: string;
    authority_id: string | null;
    is_active: boolean;
    has_dashboard_access?: boolean;
    full_name?: string;
    authority_name?: string;
}

interface AuthContextType {
    session: Session | null;
    user: User | null;
    profile: AuthProfile | null;
    profileError: Error | null;
    loading: boolean;
    profileLoading: boolean;
    signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [session, setSession] = useState<Session | null>(null);
    const [profile, setProfile] = useState<AuthProfile | null>(null);
    const [loading, setLoading] = useState(true); // Initial session check
    const [profileLoading, setProfileLoading] = useState(false);
    const [profileError, setProfileError] = useState<Error | null>(null);
    const initialized = useRef(false);

    // Simple profile fetch with timeout
    const fetchProfile = async (userId: string): Promise<AuthProfile | null> => {
        try {
            if (import.meta.env.DEV) console.log('[AUTH] Fetching profile for', userId);
            setProfileLoading(true);

            // Create timeout promise
            const timeoutPromise = new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error('Profile fetch timeout (15s)')), 15000)
            );

            // Create fetch promise
            const fetchPromise = (async () => {
                if (import.meta.env.DEV) console.log('[AUTH] Starting Supabase query...');
                const { data: pData, error: pErr } = await supabase
                    .from('profiles')
                    .select('role, authority_id, is_active, full_name')
                    .eq('id', userId)
                    .single();

                if (import.meta.env.DEV) console.log('[AUTH] Query complete:', { hasData: !!pData, error: pErr?.message });

                if (pErr) throw pErr;
                if (!pData) throw new Error('Profile not found');

                let hasDashboardAccess = pData.role === 'ADMIN';
                let authorityName = undefined;

                if (pData.authority_id) {
                    const { data: authData } = await supabase
                        .from('authorities')
                        .select('has_dashboard_access, name')
                        .eq('id', pData.authority_id)
                        .single();
                    hasDashboardAccess = !!authData?.has_dashboard_access;
                    authorityName = authData?.name;
                }

                return {
                    role: pData.role,
                    authority_id: pData.authority_id,
                    is_active: pData.is_active,
                    has_dashboard_access: hasDashboardAccess,
                    full_name: pData.full_name,
                    authority_name: authorityName
                } as AuthProfile;
            })();

            // Race between fetch and timeout
            const profileData = await Promise.race([fetchPromise, timeoutPromise]);

            if (import.meta.env.DEV) console.log('[AUTH] Profile loaded:', profileData.role);
            setProfileError(null);
            return profileData;
        } catch (err: any) {
            console.error('[AUTH] Profile fetch error:', err.message || err);
            setProfileError(err);
            return null;
        } finally {
            setProfileLoading(false);
        }
    };

    // Track if initial auth is complete
    const initComplete = useRef(false);
    // Track last loaded user ID to prevent redundant fetches check
    const lastLoadedUserId = useRef<string | null>(null);

    useEffect(() => {
        if (initialized.current) return;
        initialized.current = true;

        const initAuth = async () => {
            if (import.meta.env.DEV) console.log('[AUTH] Initializing...');

            // Get initial session - this is FAST
            const { data: { session: initialSession } } = await supabase.auth.getSession();

            if (import.meta.env.DEV) console.log('[AUTH] Initial session:', !!initialSession);
            setSession(initialSession);

            // IMPORTANT: Set loading false IMMEDIATELY after session check
            // This allows the app to render with just the session
            setLoading(false);

            // Then fetch profile in background (non-blocking)
            if (initialSession?.user) {
                const p = await fetchProfile(initialSession.user.id);
                if (p) {
                    setProfile(p);
                    lastLoadedUserId.current = initialSession.user.id;
                }
            }

            // Mark init as complete
            initComplete.current = true;
            if (import.meta.env.DEV) console.log('[AUTH] Init complete');
        };

        initAuth();

        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
            if (import.meta.env.DEV) console.log('[AUTH] State change:', event, { initComplete: initComplete.current });

            setSession(newSession);

            if (event === 'SIGNED_OUT') {
                setProfile(null);
                lastLoadedUserId.current = null;
                return;
            }

            // Skip profile fetch during initial load - initAuth handles it
            if (!initComplete.current) {
                if (import.meta.env.DEV) console.log('[AUTH] Skipping fetch during init');
                return;
            }

            // Only refetch profile on actual sign-in after init is complete
            if (event === 'SIGNED_IN' && newSession?.user) {
                // Skip if already loaded for this user to prevent tab-switch redundancy
                if (lastLoadedUserId.current === newSession.user.id) {
                    if (import.meta.env.DEV) console.log('[AUTH] Profile already loaded, skipping redundant fetch');
                    return;
                }

                const p = await fetchProfile(newSession.user.id);
                if (p) {
                    setProfile(p);
                    lastLoadedUserId.current = newSession.user.id;
                }
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    const signOut = async () => {
        await supabase.auth.signOut();
        setSession(null);
        setProfile(null);
        lastLoadedUserId.current = null;
    };

    return (
        <AuthContext.Provider value={{
            session,
            user: session?.user ?? null,
            profile,
            profileError,
            loading,
            profileLoading,
            signOut
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
